import { PaymentTypeDto } from '../../shared/payment-types/dto/payment-type.dto';
import { PaymentTypeService } from '../../shared/payment-types/payment-type.service';
import { Component, OnInit, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { UserService } from '../../../users/shared/user.service';
import { PagedResult } from '../../../shared/model/generic-paged-result.model';
import { SerialSearchExpression } from '../../../shared/search-expression/serial-search-expression.model';
import { FieldTypeEnum } from '../../../shared/search-expression/field-type.enum';
import { InvoiceService } from '../../shared/invoice/invoice.service';
import { InvoiceAdvancedSearch } from '../../shared/invoice/invoice-advanced-search.model';
import { InvoiceAdvancedSearchResult } from '../../shared/invoice/invoice-advanced-search-result.model';
import { AgingPeriodEnum } from '../../shared/invoice/aging-period.model';
import { TabsComponent, TabComponent } from '../../../shared/widgets/tabs/index';
import { ActivatedRoute } from '@angular/router';

/**
 * admin mass comm messages
 */
@Component({
  selector: 'wl-invoice-selector-form',
  templateUrl: 'invoice-selector-form.component.html',
  styleUrls: ['invoice-selector-form.component.less'],
  providers: [],
    standalone: false
})
export class InvoiceSelectorFormComponent implements OnInit {
  @Input() public searchParams: InvoiceAdvancedSearch = new InvoiceAdvancedSearch();
  @Output('result') public resultEmitter: EventEmitter<PagedResult<Array<InvoiceAdvancedSearchResult>>> = new EventEmitter<
    PagedResult<Array<InvoiceAdvancedSearchResult>>
  >();
  @Output('searching') public searchingEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output('accessDenied') public accessDeniedEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild('formTabs', { static: false }) private _tabs: TabsComponent;
  @ViewChild('tabInvoiceInfo', { static: false }) private _tabInvoiceInfo: TabComponent;

  public fieldTypeEnum = FieldTypeEnum;
  public agingPeriodEnum = AgingPeriodEnum;
  public paymentTypes: PaymentTypeDto[] = [];
  private _searching: boolean = false;
  private _accessDenied: boolean = false;

  /**
   * inject
   * @param _userService
   * @param _router
   */
  public constructor(
    private _userService: UserService,
    private _invoiceService: InvoiceService,
    private _paymentTypeService: PaymentTypeService,
    private _route: ActivatedRoute
  ) {}

  /**
   * on init
   */
  public ngOnInit(): void {
    // make sure they can use this
    this._userService.getCurrentUserSecurityElementAccessLevel('Revenue_Module').then((userAccessLevel: number) => {
      this._accessDenied = userAccessLevel < 1;
      if (this._accessDenied) this.accessDeniedEmitter.emit(true);
    });

    this._paymentTypeService.getPaymentTypes().then((paymentTypes) => {
      this.paymentTypes = paymentTypes;
    });
  }

  /**
   * run a search
   */
  public async search(): Promise<void> {
    if (!this.searchParams) return;

    if (this._searching) return;

    this.setSearchingFlag(true);

    try {
      let result = await this._invoiceService.advancedSearch(this.searchParams);
      this.resultEmitter.emit(result);
    } finally {
      this.setSearchingFlag(false);
    }
  }

  /**
   * reset the form. does not reset the search params field. that's the responsibility of the user of the component.
   */
  public resetForm(): void {
    this._tabs.select(this._tabInvoiceInfo);
  }

  /**
   * change searching flag and emit change notification if different
   * @param value
   */
  private setSearchingFlag(value: boolean): void {
    let oldValue: boolean = this._searching;
    this._searching = value;

    if (oldValue != this._searching) this.searchingEmitter.emit(this._searching);
  }

  /**
   * someone clicked add new invoice info search field
   */
  public handleAddInvoiceInfoSearchField(): void {
    if (!this.searchParams) return;

    if (!this.searchParams.InvoiceInfoSearchExpressions) this.searchParams.InvoiceInfoSearchExpressions = [];

    this.searchParams.InvoiceInfoSearchExpressions.push(new SerialSearchExpression());
  }

  /**
   * remove expression at given index
   * @param expressionIndex
   */
  public handleRemoveInvoiceInfoSearchField(expressionIndex: number): void {
    if (
      !this.searchParams ||
      !this.searchParams.InvoiceInfoSearchExpressions ||
      expressionIndex < 0 ||
      expressionIndex >= this.searchParams.InvoiceInfoSearchExpressions.length
    )
      return;

    this.searchParams.InvoiceInfoSearchExpressions.splice(expressionIndex, 1);
  }

  /**
   * someone clicked add new invoice info search field
   */
  public handleAddInvoicePaymentSearchField(): void {
    if (!this.searchParams) return;

    if (!this.searchParams.InvoicePaymentSearchExpressions) this.searchParams.InvoicePaymentSearchExpressions = [];

    this.searchParams.InvoicePaymentSearchExpressions.push(new SerialSearchExpression());
  }

  /**
   * remove expression at given index
   * @param expressionIndex
   */
  public handleRemoveInvoicePaymentSearchField(expressionIndex: number): void {
    if (
      !this.searchParams ||
      !this.searchParams.InvoicePaymentSearchExpressions ||
      expressionIndex < 0 ||
      expressionIndex >= this.searchParams.InvoicePaymentSearchExpressions.length
    )
      return;

    this.searchParams.InvoicePaymentSearchExpressions.splice(expressionIndex, 1);
  }

  public handleAgingRadioClick(event: Event): void {
    if (!event && !event.target) return;

    let newAgingPeriod: AgingPeriodEnum = <AgingPeriodEnum>(<any>(<HTMLInputElement>event.target).value);

    // fires before setting the new value to the model
    // so if it's the same, they clicked it twice
    if (this.searchParams.AgingPeriod == newAgingPeriod) newAgingPeriod = AgingPeriodEnum.None;

    setTimeout(() => (this.searchParams.AgingPeriod = newAgingPeriod));
  }
}
